import { useEffect, useRef, useState } from 'react';
import { useAppStore } from '../../store';
import { callAction } from '../../app/actions';
import type { NodeId } from '../../lib/types';

/**
 * A node's label, and the input that replaces it while it is being renamed.
 *
 * `startRename()` swapped the `<span class="txt">` for an `<input class="edit">` with
 * `replaceWith`, read the value back out on blur, and relied on the next full re-render to
 * put the span back. Two things about that do not survive the port: React owns this subtree
 * now and would undo the swap on its next render, and the typed value lived in the DOM and
 * nowhere else — the shape CLAUDE.md calls the most likely source of silent data loss, and
 * the last instance of it in the app.
 *
 * So the edit is STATE: which node is being renamed is in the store (one node at a time, by
 * construction), and the draft text is local to the input. Enter commits, Escape abandons,
 * blur commits — the same three the original bound.
 *
 * The COMMIT still goes through the vanilla script: a rename marks the node's translation
 * dirty, pushes an undo entry and saves, and all three of those are still `infostation.html`'s
 * to do. Reads move with the map; that write moves with the undo stack.
 */
export function NodeRename({ id, label }: { id: NodeId; label: string }) {
  const renaming = useAppStore((s) => s.renamingId === id);
  const setRenamingId = useAppStore((s) => s.setRenamingId);
  const [draft, setDraft] = useState(label);
  const ref = useRef<HTMLInputElement>(null);
  // Set once per sitting, so a commit on Enter is not repeated by the blur it causes.
  const done = useRef(false);

  useEffect(() => {
    if (!renaming) return;
    done.current = false;
    setDraft(label);
    const el = ref.current;
    if (el) {
      el.focus();
      el.select();
    }
    // `label` is deliberately not a dependency: re-seeding the draft from the store mid-edit
    // would overwrite what is being typed the moment anything else re-rendered the node.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renaming, id]);

  if (!renaming) return <span className="txt">{label}</span>;

  const commit = () => {
    if (done.current) return;
    done.current = true;
    setRenamingId(null);
    callAction('renameNode', id, draft);
  };

  return (
    <input
      ref={ref}
      className="edit"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
        if (e.key === 'Escape') {
          // Abandon: the original restored the input's value and blurred, which committed the
          // unchanged label. Same outcome, without the round trip.
          done.current = true;
          setRenamingId(null);
        }
      }}
      // The stage's pointerdown would otherwise start a drag from inside the input.
      onPointerDown={(e) => e.stopPropagation()}
    />
  );
}
